'use client';

import type { CustomFilter } from '@/components/table-filters/constants';
import { usePerms } from '@/hooks/usePerms';
import { api } from '@/lib/api';
import { resolveGlobalRealestateIds } from '@/lib/global-filter/get-global-filter-query';
import { useReadGlobalFilter } from '@/lib/global-filter/hooks/use-global-filter';
import { Urls } from '@/lib/urls';
import { getQueryClient } from '@/providers/queryClient';
import { useQuery } from '@tanstack/react-query';
import { INVOICE_STATUS_FILTER_ID } from '../filters';

type InvoiceListKind = 'rental' | 'non_rental';

function nonAttestedDebtCountQueryOptions({
  realestateIds,
  enabled,
}: {
  realestateIds: string[] | null | undefined;
  enabled?: boolean;
}) {
  return api.accounting_debtinvoice.query.count.queryOptions({
    filters: {
      attestedDate: { isnull: true },
      closedTime: { isnull: true },
      ...(realestateIds?.length ? { globalRealestates: realestateIds } : {}),
    },
    enabled,
  });
}

export async function fetchNonAttestedDebtCount(): Promise<number> {
  const realestateIds = await resolveGlobalRealestateIds();
  const result = await getQueryClient().fetchQuery(
    nonAttestedDebtCountQueryOptions({ realestateIds })
  );
  return result.statistics.count;
}

/**
 * Non-attested, open reminders and debt invoices, shared by the chip and
 * the Snabbfilter link.
 */
export function useNonAttestedDebt({ kind }: { kind: InvoiceListKind }) {
  const { canView } = usePerms('debtinvoice');

  const globalFilter = useReadGlobalFilter();
  const globalRealestates = useQuery({
    queryKey: ['non-attested-debt', 'global-realestates', globalFilter],
    queryFn: () => resolveGlobalRealestateIds(),
    enabled: canView,
  });

  const countQuery = useQuery(
    nonAttestedDebtCountQueryOptions({
      realestateIds: globalRealestates.data,
      enabled: canView && globalRealestates.isSuccess,
    })
  );

  return {
    canView,
    count: countQuery.data?.statistics.count ?? 0,
    isLoading: canView && (globalRealestates.isPending || countQuery.isPending),
    href: `${Urls.dashboard.invoicing.nonAttestedDebthandling}?from=${kind}`,
  };
}

export function useNonAttestedDebtCustomFilter({
  kind,
}: {
  kind: InvoiceListKind;
}): CustomFilter[] {
  const { canView, count, isLoading, href } = useNonAttestedDebt({ kind });

  if (!canView) return [];

  return [
    {
      filterId: INVOICE_STATUS_FILTER_ID,
      afterChoiceId: 'not_attested',
      title: 'Ej attesterade påminnelser och krav',
      href,
      count,
      isLoadingCount: isLoading,
      variant: 'yellow',
    } satisfies CustomFilter,
  ];
}
